// ============================================
// BRANDCONNECT LAYOUT - Influencer.in Design
// Standalone layout with TopNav + Planner Sidebar
// Ported from Influencer.in-Brandconnect-dashboard
// ============================================

import React from 'react';
import { Link, useLocation } from 'react-router-dom';

// -------------------- Helpers --------------------


// -------------------- Styles --------------------

const styles = {
    wrapper: {
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#f9fafb',
        overflow: 'hidden',
    },
    topNav: {
        height: 64,
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        flexShrink: 0,
        zIndex: 100,
    },
    logo: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        textDecoration: 'none',
    },
    logoIcon: {
        width: 32,
        height: 32,
        background: '#111827',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 12,
        fontWeight: 700,
    },
    logoText: {
        fontSize: 18,
        fontWeight: 600,
        color: '#111827',
        letterSpacing: '-0.5px',
    },
    navLinks: {
        display: 'flex',
        alignItems: 'center',
        gap: 32,
        listStyle: 'none',
        margin: 0,
        padding: 0,
    },
    navLinkActive: {
        fontSize: 14,
        fontWeight: 500,
        color: '#111827',
        textDecoration: 'none',
    },
    navLink: {
        fontSize: 14,
        fontWeight: 500,
        color: '#6b7280',
        textDecoration: 'none',
    },
    rightActions: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    downloadBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 16px',
        background: '#fbbf24',
        border: 'none',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        color: '#111827',
        cursor: 'pointer',
    },
    helpBtn: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: '#f3f4f6',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#6b7280',
        cursor: 'pointer',
        fontSize: 14,
    },
    avatar: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: '#fef3c7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#b45309',
        fontWeight: 600,
        fontSize: 14,
    },
    body: {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
    },
    sidebar: {
        width: 240,
        background: '#fff',
        borderRight: '1px solid #e5e7eb',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflowY: 'auto',
    },
    sideNav: {
        flex: 1,
        padding: '16px 0',
    },
    sideNavLink: (isActive) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 20px',
        fontSize: 14,
        textDecoration: 'none',
        transition: 'background 0.15s',
        borderLeft: isActive ? '4px solid #f59e0b' : '4px solid transparent',
        background: isActive ? '#fffbeb' : 'transparent',
        color: isActive ? '#b45309' : '#4b5563',
        fontWeight: isActive ? 600 : 400,
    }),
    bcSection: {
        marginTop: 24,
        paddingTop: 16,
        borderTop: '1px solid #f3f4f6',
    },
    bcHeader: {
        padding: '0 20px 8px',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
    },
    bcLabel: {
        fontSize: 10,
        fontWeight: 700,
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
    },
    bcBadge: {
        padding: '1px 6px',
        background: '#dc2626',
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        borderRadius: 4,
    },
    bcConnectBtn: {
                display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 12px',
        background: '#fffbeb',
        border: '1px solid #fde68a',
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 500,
        color: '#b45309',
        cursor: 'pointer',
        margin: '0 16px',
        width: 'calc(100% - 32px)',
        textAlign: 'left',
    },
    bcDot: (connected) => ({
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: connected ? '#22c55e' : '#fbbf24',
        flexShrink: 0,
    }),
    creditBox: {
        margin: 12,
        padding: 16,
        background: '#111827',
        color: '#fff',
        borderRadius: 12,
    },
    main: {
        flex: 1,
        overflowY: 'auto',
    },
};

// -------------------- BC Connection Modal --------------------


// -------------------- TopNav --------------------

function TopNav() {
    const location = useLocation();
    const navItems = [
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'My Campaigns', href: '/brand/campaigns' },
        { label: 'Planner', href: '/planner/discover' },
        { label: 'Competitor Insights', href: '/competitor-insights/' },
    ];

    return (
        <header style={styles.topNav}>
            {/* Logo */}
            <Link to="/" style={styles.logo}>
                <div style={styles.logoIcon}>i.</div>
                <span style={styles.logoText}>INFLUENCER</span>
            </Link>

            {/* Nav links */}
            <nav>
                <ul style={styles.navLinks}>
                    {navItems.map((item) => {
                        const isActive = location.pathname.startsWith(item.href);
                        return (
                            <li key={item.href}>
                                <Link
                                    to={item.href}
                                    style={isActive ? styles.navLinkActive : styles.navLink}
                                >
                                    {item.label}
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            {/* Right */}
            <div style={styles.rightActions}>
                <button style={styles.downloadBtn}>
                    <span>Prod Environment</span>
                </button>
                {/* <button style={styles.downloadBtn}>
                    <span>Download Extension</span>
                </button> */}
                <button style={styles.helpBtn}>?</button>
                <div style={styles.avatar}>U</div>
            </div>
        </header>
    );
}

// -------------------- Sidebar --------------------

function PlannerSidebar() {
    const location = useLocation();

    const navItems = [
        { icon: '🔍', label: 'Discover', href: '/brandconnect/discover' },
        { icon: '📋', label: 'Collab List', href: '/brandconnect/collab' },
        { icon: '📢', label: 'Campaign', href: '/brand/campaigns', extraMatch: '/brand/campaigns' },
        { icon: '📊', label: 'Competitors Analysis', href: '/competitor-insights/' },
        { icon: '📁', label: 'Curated List', href: '/planner/curated-list' },
    ];

    return (
        <aside style={styles.sidebar}>
            <nav style={styles.sideNav}>
                {navItems.map((item) => {
                    const isActive = location.pathname === item.href ||
                        (item.href !== '/' && location.pathname.startsWith(item.href)) ||
                        (item.extraMatch && location.pathname.startsWith(item.extraMatch));
                    return (
                        <Link key={item.href} to={item.href} style={styles.sideNavLink(isActive)}>
                            <span>{item.icon}</span>
                            <span>{item.label}</span>
                        </Link>
                    );
                })}

                {/* BrandConnect Section */}
                {/* <div style={styles.bcSection}>
                    <div style={styles.bcHeader}>
                        <span style={styles.bcLabel}>Creator Partnerships</span>
                        <span style={styles.bcBadge}>YCP</span>
                    </div>

                    {bcConnected ? (
                        <div style={{ margin: '0 16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, marginBottom: 4 }}>
                                <div style={styles.bcDot(true)} />
                                <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#15803d' }}>Connected{bcBrand ? ` · ${bcBrand}` : ''}</span>
                                <button onClick={() => setShowDisconnect(!showDisconnect)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#9ca3af' }}>⋮</button>
                            </div>
                            {showDisconnect && (
                                <button onClick={() => { setBcConnected(false); setBcBrand(''); setShowDisconnect(false); }}
                                    style={{ width: '100%', padding: '7px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                                    Disconnect Creator Partnerships
                                </button>
                            )}
                        </div>
                    ) : (
                        <button onClick={() => setShowModal(true)} style={styles.bcConnectBtn}>
                            <div style={styles.bcDot(false)} />
                            <span style={{ flex: 1 }}>Connect Account</span>
                            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#fbbf24' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                        </button>
                    )}

                    <BCConnectionModal
                        isOpen={showModal}
                        onClose={() => setShowModal(false)}
                        onConnect={({ brandName }) => { setBcConnected(true); setBcBrand(brandName); }}
                    />
                </div> */}
            </nav>

            {/* Credit balance */}
            <div style={{ padding: 12 }}>
                <div style={styles.creditBox}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span>📦</span>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>Credit balance</span>
                        <span style={{ marginLeft: 'auto', color: '#fbbf24', fontSize: 14 }}>ℹ</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af' }}>Premium profiles: 21</div>
                </div>
            </div>
        </aside>
    );
}

// -------------------- Layout --------------------

export function BrandConnectLayout({ children, showSidebar = true }) {
    return (
        <div style={styles.wrapper}>
            <TopNav />
            <div style={styles.body}>
                {showSidebar && <PlannerSidebar />}
                <main style={styles.main}>
                    {children}
                </main>
            </div>
        </div>
    );
}

export default BrandConnectLayout;
